import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ArrowRight, CalendarPlus, Check, Download, RotateCcw } from "lucide-react";
import CalendarView from "./BookingCalendar";
import Reveal from "./Reveal";
import { DEFAULT_AVAILABILITY, INTENT_OPTIONS, TIMEFRAME_OPTIONS, fmtDateLong, fmtTime } from "@/lib/booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-smoke";
const inputCls =
  "w-full border border-hairline bg-white px-4 py-3 text-[15px] text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-ink focus:ring-1 focus:ring-ink";

const INTENT_TESTIDS = {
  "Looking to Buy": "radio-buy",
  "Looking to Lease": "radio-lease",
  "Not sure yet": "radio-unsure",
};

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  address: "",
  timeframe: "",
  intent: "",
  consent: false,
};

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelCls}>
        {label}
      </label>
      {children}
    </div>
  );
}

export default function BookingSection() {
  const [monthOffset, setMonthOffset] = useState(0);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [availability, setAvailability] = useState(DEFAULT_AVAILABILITY);

  useEffect(() => {
    axios
      .get(`${API}/availability`)
      .then(({ data }) => setAvailability(data))
      .catch(() => {});
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    if (!slot) return "Please choose a date and time for your consultation.";
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (form.phone.trim().length < 7) return "Please enter a valid phone number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address.";
    if (form.address.trim().length < 5) return "Please enter your property address.";
    if (!form.timeframe) return "Please select your selling timeframe.";
    if (!form.intent) return "Please tell us if you're looking to buy or lease.";
    if (!form.consent) return "Please agree to be contacted so we can confirm your booking.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    setError(msg);
    if (msg) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        timeframe: form.timeframe,
        intent: form.intent,
        slot_utc: slot.utc.toISOString(),
        consent: true,
      });
      setConfirmation(data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Something went wrong booking your consultation. Please try again or call (647) 535-9455."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setConfirmation(null);
    setForm(emptyForm);
    setSlot(null);
    setDate(null);
    setMonthOffset(0);
    setError("");
  };

  return (
    <section id="book" className="bg-ivory py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-ink">
            Book a Consultation
          </p>
          <h2 className="font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Reserve your thirty minutes with Will.
          </h2>
          <p className="mt-4 text-[15px] font-light leading-relaxed text-smoke sm:text-base">
            Pick a time, share the details of your property, and your calendar invite arrives by
            email instantly — for you and for Will.
          </p>
        </Reveal>

        <AnimatePresence mode="wait">
          {confirmation ? (
            <motion.div
              key="confirmation"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto mt-12 max-w-3xl border border-hairline bg-white p-8 sm:p-12"
              data-testid="booking-confirmation"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ink text-ivory">
                  <Check size={22} />
                </span>
                <div>
                  <h3 className="font-serif text-2xl text-ink sm:text-3xl">Your consultation is booked.</h3>
                  <p className="text-sm font-light text-smoke">
                    A confirmation email with your calendar invite is on its way to {form.email}.
                  </p>
                </div>
              </div>

              <dl className="mt-8 grid grid-cols-1 gap-x-10 gap-y-4 border-t border-hairline pt-8 sm:grid-cols-2">
                {[
                  ["When", `${fmtDateLong(slot.utc)} · ${fmtTime(slot.utc)} (Toronto) — 30 minutes`],
                  ["Consultation with", "Will French, REALTOR® — Team Partner"],
                  ["Name", form.name],
                  ["Property address", form.address],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.24em] text-smoke">{k}</dt>
                    <dd className="mt-1 text-[15px] font-medium text-ink">{v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href={confirmation.gcal_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-calendar-add-btn"
                  className="flex items-center gap-2 bg-ink px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-ink/80"
                >
                  <CalendarPlus size={14} />
                  Add to Google Calendar
                </a>
                <a
                  href={confirmation.ics_url}
                  data-testid="download-ics-btn"
                  className="flex items-center gap-2 border border-ink px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-white"
                >
                  <Download size={14} />
                  Download invite (.ics)
                </a>
              </div>

              <button
                type="button"
                onClick={reset}
                data-testid="book-another-btn"
                className="mt-7 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-smoke transition-colors hover:text-ink"
              >
                <RotateCcw size={13} />
                Book another time
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[0.95fr_1.05fr]"
            >
              <Reveal className="border border-hairline bg-white p-7 sm:p-9">
                <p className="mb-6 text-[11px] font-semibold uppercase tracking-[0.28em] text-ink/60">
                  01 — Choose a time
                </p>
                <CalendarView
                  monthOffset={monthOffset}
                  setMonthOffset={setMonthOffset}
                  date={date}
                  onDate={setDate}
                  slot={slot}
                  onSlot={setSlot}
                  av={availability}
                />
              </Reveal>

              <Reveal delay={0.1} className="border border-hairline bg-white p-7 sm:p-9">
                <p className="mb-6 text-[11px] font-semibold uppercase tracking-[0.28em] text-ink/60">
                  02 — Your details
                </p>

                <div
                  className={[
                    "mb-7 flex items-center gap-3 border px-4 py-3",
                    slot ? "border-hairline bg-ivory" : "border-dashed border-ink/30 bg-ivory",
                  ].join(" ")}
                  data-testid="booking-selected-slot-summary"
                >
                  <span className="h-2 w-2 rotate-45 bg-gold" />
                  {slot ? (
                    <p className="text-sm text-ink">
                      You're booking <strong>{fmtDateLong(slot.utc)}</strong> at <strong>{fmtTime(slot.utc)}</strong> (Toronto)
                    </p>
                  ) : (
                    <p className="text-sm text-smoke">Pick a date and time to start your booking.</p>
                  )}
                </div>

                <form onSubmit={onSubmit} noValidate>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Name" htmlFor="f-name">
                      <input
                        id="f-name"
                        type="text"
                        data-testid="input-name"
                        className={inputCls}
                        placeholder="Your full name"
                        value={form.name}
                        onChange={set("name")}
                      />
                    </Field>
                    <Field label="Phone" htmlFor="f-phone">
                      <input
                        id="f-phone"
                        type="tel"
                        data-testid="input-phone"
                        className={inputCls}
                        placeholder="(416) 000-0000"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Email" htmlFor="f-email">
                      <input
                        id="f-email"
                        type="email"
                        data-testid="input-email"
                        className={inputCls}
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={set("email")}
                      />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Address" htmlFor="f-address">
                      <input
                        id="f-address"
                        type="text"
                        data-testid="input-address"
                        className={inputCls}
                        placeholder="Property address"
                        value={form.address}
                        onChange={set("address")}
                      />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Timeframe" htmlFor="f-timeframe">
                      <select
                        id="f-timeframe"
                        data-testid="select-timeframe"
                        className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;charset=utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cpath%20fill%3D%22%236B6A68%22%20d%3D%22M4.4%207.4l3.4 3.4a.25.25 0 00.35 0l3.4-3.4A.25.25 0 0011.4 7H4.6a.25.25 0 00-.2.4z%22%2F%3E%3C%2Fsvg%3E')] bg-[position:right_14px_center] bg-no-repeat pr-10`}
                        value={form.timeframe}
                        onChange={set("timeframe")}
                      >
                        <option value="" disabled>
                          Select a timeframe
                        </option>
                        {TIMEFRAME_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div className="mt-5">
                    <span className={labelCls}>Are you looking to buy or lease your next home?</span>
                    <div className="flex flex-wrap gap-2">
                      {INTENT_OPTIONS.map((opt) => {
                        const active = form.intent === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            data-testid={INTENT_TESTIDS[opt]}
                            onClick={() => setForm((f) => ({ ...f, intent: opt }))}
                            className={[
                              "border px-4 py-2.5 text-[12px] font-medium transition-colors",
                              active
                                ? "border-ink bg-ink text-white"
                                : "border-hairline text-ink hover:border-ink",
                            ].join(" ")}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <label className="mt-6 flex cursor-pointer items-start gap-3" htmlFor="f-consent">
                    <input
                      id="f-consent"
                      type="checkbox"
                      data-testid="consent-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-black"
                      checked={form.consent}
                      onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    />
                    <span className="text-[11px] leading-relaxed text-smoke">
                      I agree to be contacted by CHK Real Estate via call, email, and text for real
                      estate services. To opt out, you can reply 'stop' at any time or reply 'help'
                      for assistance. Message and data rates may apply.
                    </span>
                  </label>

                  {error && (
                    <div
                      className="mt-5 flex items-start gap-2 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                      data-testid="booking-error-alert"
                      role="alert"
                    >
                      <AlertCircle size={15} className="mt-0.5 shrink-0" />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-consultation-btn"
                    className="group mt-7 flex w-full items-center justify-center gap-3 bg-ink px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-white transition-all hover:bg-ink/85 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? "Booking your time..." : "Confirm consultation"}
                    {!submitting && (
                      <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                    )}
                  </button>
                </form>
              </Reveal>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}